/**
 * Copyright 2016-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */
import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from 'react-redux';
import ConfigUtils from 'qwc2/utils/ConfigUtils';
import LocaleUtils from 'qwc2/utils/LocaleUtils';
import Icon from 'qwc2/components/Icon';
import IdentifyViewer from 'qwc2/components/IdentifyViewer';
import 'qwc2/components/style/IdentifyViewer.css';
import {addDetailWindow} from '../../actions/details';
import {addForIndicators, highlightSelected} from '../../actions/indicators';
import FeatureUtils from '../../utils/FeatureUtils';
import TypeUtils from '../../utils/TypeUtils';
import ErrorBoundary, {inlineErrorFallback} from '../../components/ErrorBoundary';

// Calls the render function within its own component, so that an error is caught by a surrounding boundary
const Deferred = ({render}) => render();

/**
 * Wrapper around the upstream IdentifyViewer.
 *
 * Results of objects (feature.properties.objectType set) are not shown as attribute table but
 * as buttons (details, add component, select object for indicators). All other results are
 * passed unchanged to the upstream viewer (display modes, export, compare, links, ...).
 *
 * Extra attributes (former `attributeCalculator` prop) are provided via
 * `window.qwc2.__attributeCalculators`, see appConfig.js.
 */
class CustomIdentifyViewer extends React.Component {
    static propTypes = {
        addDetailWindow: PropTypes.func,
        addForIndicators: PropTypes.func,
        attributeCalculator: PropTypes.func,
        clearResults: PropTypes.func,
        clickPoint: PropTypes.array,
        highlightSelected: PropTypes.func,
        identifyResults: PropTypes.object,
        resultsChanged: PropTypes.func
    };
    constructor(props) {
        super(props);
        this.registerAttributeCalculator();
    }
    registerAttributeCalculator = () => {
        // window.qwc2 is (re)created by the API plugin, so register lazily when the viewer is shown
        const calculators = window.qwc2?.__attributeCalculators;
        if (calculators && this.props.attributeCalculator) {
            calculators.customAttributeCalculator = {calc: this.props.attributeCalculator, before: false};
        }
    };
    isObjectResult =(feature) => {
        return !!feature.properties?.objectType;
    };
    splitResults = () => {
        const objectResults = {};
        const otherResults = {};
        Object.entries(this.props.identifyResults).forEach(([layerid, features]) => {
            const objects = features.filter(this.isObjectResult);
            const others = features.filter(f => !this.isObjectResult(f));
            if (objects.length > 0) {
                objectResults[layerid] = objects;
            }
            if (others.length > 0) {
                otherResults[layerid] = others;
            }
        });
        return {objectResults, otherResults};
    };
    otherResultsChanged = (results) => {
        // Merge changes of the upstream viewer with the object results
        const {objectResults} = this.splitResults();
        this.props.resultsChanged({...objectResults, ...results});
    };
    removeObjectResult = (layerid, feature) => {
        const newResults = {...this.props.identifyResults};
        newResults[layerid] = newResults[layerid].filter(f => f !== feature);
        if (newResults[layerid].length === 0) {
            delete newResults[layerid];
        }
        this.props.resultsChanged(newResults);
    };
    addComponentWindow = (data) => {
        const component = {
            detailWindowType: 'component',
            addComponentTo: 'feature',
            clickPoint: this.props.clickPoint,
            belongsTo: data,
            types: {
                typeLabel: LocaleUtils.trmsg("featuredetails.addcomponent")
            },
            properties: {
                id: null,
                uuid: null
            }
        };
        this.props.clearResults();
        this.props.addDetailWindow(component);
    };
    addDetailWindow = (data) => {
        this.props.clearResults();
        this.props.addDetailWindow(data);
    };
    addForIndicators = (data) => {
        const url = ConfigUtils.getConfigProp("featureServiceUrl") + '/' + data.properties.linkedObjectType + '/items/' + data.properties.linkedObjectUuid;
        axios(url).then(response => {
            const linkedObject = FeatureUtils.getSingleFeature(response.data);
            if (linkedObject) {
                this.props.addForIndicators({...data, linkedObject: linkedObject});
                this.props.highlightSelected();
            } else {
                // eslint-disable-next-line no-alert
                alert(LocaleUtils.tr("indicators.objectnoloaded"));
            }
        }).catch(error => {
            // eslint-disable-next-line no-console
            console.log("Error *** : " + error);
        });
    };
    renderObjectResult = (layerid, feature) => {
        let resultTitle = feature.displayname;
        if (feature.layertitle) {
            resultTitle = feature.layertitle + ": " + feature.displayname;
        }
        resultTitle = TypeUtils.getObjectLabel(feature.properties.objectType, feature) || resultTitle;
        return (
            <div className="identify-result-frame" key={layerid + "$" + feature.id}>
                <div className="identify-result-title">
                    <span>{resultTitle}</span>
                    <Icon icon="trash" onClick={() => this.removeObjectResult(layerid, feature)} title={LocaleUtils.tr("identify.removeresult")} />
                </div>
                <div className="identify-result-container">
                    <button className="button" onClick={() => this.addDetailWindow(feature)}>{LocaleUtils.tr('featuredetails.details')}</button>
                    {feature.addComponents ? (
                        <button className="button" onClick={() => this.addComponentWindow(feature)}>{LocaleUtils.tr('featuredetails.addcomponent')}</button>
                    ) : null}
                    {feature.properties.objectType === 'indicatorvalue' ? (
                        <button className="button" onClick={() => this.addForIndicators(feature)}>{LocaleUtils.tr('indicators.selectobject')}</button>
                    ) : null}
                </div>
            </div>
        );
    };
    render() {
        const {objectResults, otherResults} = this.splitResults();
        const {addDetailWindow, addForIndicators, attributeCalculator, clearResults, clickPoint, highlightSelected, identifyResults, resultsChanged, ...viewerProps} = this.props;
        if (Object.keys(objectResults).length === 0) {
            return (<IdentifyViewer {...viewerProps} identifyResults={identifyResults} resultsChanged={resultsChanged} />);
        }
        const hasOthers = Object.keys(otherResults).length > 0;
        return (
            <div className="identify-body">
                <div className="identify-flat-results-list" style={hasOthers ? {flex: '0 0 auto', maxHeight: '40%'} : undefined}>
                    {Object.entries(objectResults).map(([layerid, features]) => features.map(f => (
                        <ErrorBoundary fallback={inlineErrorFallback} key={layerid + "$" + f.id} name="Identify-Ergebnis">
                            <Deferred render={() => this.renderObjectResult(layerid, f)} />
                        </ErrorBoundary>
                    )))}
                </div>
                {hasOthers ? (
                    <div style={{flex: '1 1 auto', minHeight: 0}}>
                        <IdentifyViewer {...viewerProps} identifyResults={otherResults} resultsChanged={this.otherResultsChanged} />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default connect(() => ({}), {
    addDetailWindow: addDetailWindow,
    addForIndicators: addForIndicators,
    highlightSelected: highlightSelected
})(CustomIdentifyViewer);
